import axios from "axios";
import { useEffect, useState } from "react";

// Projects
export const listProjects = () => axios.get("/projects").then(r => r.data);
export const createProject = (body) => axios.post("/projects", body).then(r => r.data);
export const getProject = (id) => axios.get(`/projects/${id}`).then(r => r.data);
export const updateProject = (id, body) => axios.patch(`/projects/${id}`, body).then(r => r.data);
export const deleteProject = (id) => axios.delete(`/projects/${id}`).then(r => r.data);
export const getAccounting = (id) => axios.get(`/projects/${id}/accounting`).then(r => r.data);

// Payments
export const createPayment = (pid, body) => axios.post(`/projects/${pid}/payments`, body).then(r => r.data);
export const deletePayment = (id) => axios.delete(`/payments/${id}`).then(r => r.data);

// Permits
export const listPermits = (pid) => axios.get(`/projects/${pid}/permits`).then(r => r.data);
export const createPermit = (pid, body) => axios.post(`/projects/${pid}/permits`, body).then(r => r.data);
export const updatePermit = (id, body) => axios.patch(`/permits/${id}`, body).then(r => r.data);
export const deletePermit = (id) => axios.delete(`/permits/${id}`).then(r => r.data);

// Drawings
export const listDrawings = (pid) => axios.get(`/projects/${pid}/drawings`).then(r => r.data);
export const uploadDrawing = (pid, formData) =>
  axios.post(`/projects/${pid}/drawings`, formData, { headers: { "Content-Type": "multipart/form-data" } }).then(r => r.data);
export const updateDrawing = (id, body) => axios.patch(`/drawings/${id}`, body).then(r => r.data);
export const deleteDrawing = (id) => axios.delete(`/drawings/${id}`).then(r => r.data);
export const drawingDownloadUrl = (id) => {
  const token = localStorage.getItem("boq_token");
  const base = axios.defaults.baseURL;
  return `${base}/drawings/${id}/download?auth=${encodeURIComponent(token || "")}`;
};

// Change orders
export const listChangeOrders = (pid) => axios.get(`/projects/${pid}/change-orders`).then(r => r.data);
export const createChangeOrder = (pid, body) => axios.post(`/projects/${pid}/change-orders`, body).then(r => r.data);
export const updateChangeOrder = (id, body) => axios.patch(`/change-orders/${id}`, body).then(r => r.data);
export const deleteChangeOrder = (id) => axios.delete(`/change-orders/${id}`).then(r => r.data);

// Snags
export const listSnags = (pid) => axios.get(`/projects/${pid}/snags`).then(r => r.data);
export const createSnag = (pid, body) => axios.post(`/projects/${pid}/snags`, body).then(r => r.data);
export const updateSnag = (id, body) => axios.patch(`/snags/${id}`, body).then(r => r.data);
export const deleteSnag = (id) => axios.delete(`/snags/${id}`).then(r => r.data);

// Catalogs
export const listCatalogs = () => axios.get("/catalogs").then(r => r.data);
export const addCatalog = (body) => axios.post("/catalogs", body).then(r => r.data);
export const deleteCatalog = (id) => axios.delete(`/catalogs/${id}`).then(r => r.data);

export const uploadCover = (pid, formData) =>
  axios.post(`/projects/${pid}/cover`, formData, { headers: { "Content-Type": "multipart/form-data" } }).then(r => r.data);
export const coverUrl = (pid, coverId) => {
  if (!coverId) return null;
  const token = localStorage.getItem("boq_token");
  const base = axios.defaults.baseURL;
  return `${base}/projects/${pid}/cover?auth=${encodeURIComponent(token || "")}&v=${coverId}`;
};

// React hook — returns a data-URL cached in localStorage after first fetch,
// so cover images load instantly on subsequent renders and page reloads.
// Cache is busted whenever the coverId changes (upload → new id).
export function useCachedCover(pid, coverId) {
  const key = coverId ? `nh_cover_${coverId}` : null;
  const [url, setUrl] = useState(() => {
    if (!key) return null;
    try { return localStorage.getItem(key) || coverUrl(pid, coverId); }
    catch { return coverUrl(pid, coverId); }
  });
  useEffect(() => {
    let cancelled = false;
    if (!key || !pid || !coverId) { setUrl(null); return; }
    try {
      const cached = localStorage.getItem(key);
      if (cached) { setUrl(cached); return; }
    } catch {}
    // Fetch and cache
    setUrl(coverUrl(pid, coverId));
    const token = localStorage.getItem("boq_token");
    const base = axios.defaults.baseURL;
    fetch(`${base}/projects/${pid}/cover?v=${coverId}`, {
      headers: { Authorization: `Bearer ${token || ""}` },
    })
      .then(r => r.ok ? r.blob() : Promise.reject(r.status))
      .then(blob => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      }))
      .then(dataUrl => {
        if (cancelled) return;
        try {
          // Trim old covers before writing new one to stay under quota
          for (const k of Object.keys(localStorage)) {
            if (k.startsWith("nh_cover_") && k !== key && Math.random() < 0.1) {
              localStorage.removeItem(k);
            }
          }
          localStorage.setItem(key, dataUrl);
        } catch {}
        setUrl(dataUrl);
      })
      .catch(() => { /* fall back to direct URL already set */ });
    return () => { cancelled = true; };
  }, [pid, coverId, key]);
  return url;
}

// BOQ close
export const closeBoq = (id, body) => axios.post(`/boqs/${id}/close`, body).then(r => r.data);
export const reopenBoq = (id) => axios.post(`/boqs/${id}/reopen`).then(r => r.data);

// Vendors (scoped)
export const listVendorsForProject = (pid) => axios.get(`/vendors?project_id=${pid}`).then(r => r.data);
export const createVendorForProject = (pid, body) => axios.post(`/vendors`, { ...body, project_id: pid }).then(r => r.data);
